// DESCRIPTION: Event chip rendered inside a calendar day cell or time slot.

import React from 'react';
import { formatTime, getDisplayTime } from './utils';

const config = window.nemtlyCalendar || {};

/**
 * Resolve the color for an event chip bar based on the colorBy setting.
 *
 * @param {Object} event Calendar event object from the API.
 *
 * @return {string|undefined} Hex color string or undefined (falls back to CSS default).
 */
function getChipColor( event ) {
	const colorBy = config.colorBy || 'category';
	const categoryColors = config.categoryColors || {};

	if (
		colorBy === 'category' &&
		event.categories &&
		event.categories.length
	) {
		// Use the color of the first category that has an assigned color.
		for ( const cat of event.categories ) {
			const id = typeof cat === 'object' ? cat.id : cat;
			if ( categoryColors[ id ] ) {
				return categoryColors[ id ];
			}
		}
	}

	return undefined;
}

/**
 * EventCell
 *
 * @param {Object}  props
 * @param {Object}  props.event         Calendar event object from the API.
 * @param {boolean} props.isAllDay      Whether this is a full-day event (renders as banner).
 * @param {Object}  props.spanStyle     Optional inline style for multi-day spanning (grid-column).
 * @param {string}  props.spanClassName Optional extra class for spanning events.
 * @param {boolean} props.useLocalTz    Whether to show times in browser's local timezone.
 */
export default function EventCell( {
	event,
	isAllDay,
	spanStyle,
	spanClassName,
	useLocalTz,
} ) {
	const chipColor = getChipColor( event );
	const eventUrl =
		event.url ||
		( event.slug
			? `${ window.location.origin }/events/${ event.slug }/`
			: '' );

	// For regular chips, the color becomes the left border accent.
	// For all-day chips, the color becomes the background.
	const chipStyle = {};
	if ( chipColor ) {
		if ( isAllDay ) {
			chipStyle.backgroundColor = chipColor;
			chipStyle.borderLeftColor = chipColor;
			chipStyle.color = '#fff';
		} else {
			chipStyle.borderLeftColor = chipColor;
		}
	}

	const chipClasses = [
		'nb-calendar-event-chip',
		event.is_past ? 'nb-calendar-event-chip--past' : '',
		isAllDay ? 'nb-calendar-event-chip--all-day' : '',
	]
		.filter( Boolean )
		.join( ' ' );

	const wrapperClasses = [
		'nb-calendar-event-chip-wrapper',
		spanClassName || '',
	]
		.filter( Boolean )
		.join( ' ' );

	return (
		<div
			className={ wrapperClasses }
			style={ { position: 'relative', ...( spanStyle || {} ) } }
		>
			<a
				className={ chipClasses }
				style={ chipStyle }
				href={ eventUrl || '#' }
				aria-label={ event.title }
				title={ event.title }
			>
				<span className="nb-calendar-event-chip__title">
					{ event.title }
				</span>
				{ config.showEventTime !== false && event.time && (
					<span className="nb-calendar-event-chip__time">
						{ formatTime( getDisplayTime( event, useLocalTz ) ) }
					</span>
				) }
			</a>
		</div>
	);
}
